import api, { ApiResponse } from './api';

import { Invoice } from 'entities/Invoice';
import {
  Client,
  GetClientInvoiceListProps,
  GetClientInvoiceProps,
  SubscriptionStatus,
  UpdateClientAdminProps,
  UpdateClientInvoiceProps,
} from 'entities/Client';

interface GetClientListResponse {
  success: boolean;
  data: Client[];
}

export const getClientListService = (): ApiResponse<GetClientListResponse> =>
  api({
    method: 'GET',
    url: 'clients',
    useToken: true,
  });

interface GetClientInvoiceListResponse {
  success: boolean;
  data: Invoice[];
}

export const getClientInvoiceListService = ({
  clientId,
}: GetClientInvoiceListProps): ApiResponse<GetClientInvoiceListResponse> =>
  api({
    method: 'GET',
    url: `clients/${clientId}/invoices`,
    useToken: true,
  });

interface GetClientInvoiceResponse {
  success: boolean;
  data: Invoice;
}

export const getClientInvoiceService = ({
  clientId,
  invoiceId,
}: GetClientInvoiceProps): ApiResponse<GetClientInvoiceResponse> =>
  api({
    method: 'get',
    url: `clients/${clientId}/invoices/${invoiceId}`,
    useToken: true,
  });

interface UpdateClientInvoiceResponse {
  success: boolean;
  data: Invoice;
}

export const updateClientInvoiceService = ({
  clientId,
  invoiceId,
  file,
}: UpdateClientInvoiceProps): ApiResponse<UpdateClientInvoiceResponse> => {
  const data = new FormData();

  data.append('csv', file);
  return api({
    method: 'PATCH',
    url: `clients/${clientId}/invoices/${invoiceId}`,
    data,
    hasFile: true,
    useToken: true,
  });
};

interface UpdateClientResponse {
  success: boolean;
  data: Client;
}

export const updateClientAdminService = (
  data: UpdateClientAdminProps
): ApiResponse<UpdateClientResponse> =>
  api({
    method: 'PATCH',
    url: `clients/${data.clientId}`,
    data: { isAdmin: data.isAdmin, password: data.password },
    useToken: true,
  });

// ─── Faturamento manual (admin) ──────────────────────────────────────────────

// Tipo canônico em entities/Client — reexportado aqui para os consumidores antigos
export type { SubscriptionStatus };

export interface BillingReportItem {
  userId: string;
  userName: string;
  userEmail: string;
  documentNumber: string | null;
  phone: string | null;
  isAdmin: boolean;
  lastLogin: string | null;
  invoicesCount: number;
  planName: string;
  planPrice: number;
  subscriptionStatus: SubscriptionStatus;
  statusChangedAt: string | null;
  groupACount: number;
  groupBCount: number;
  additionalGroupACount: number;
  additionalGroupBCount: number;
  // UCs adicionais cobradas manualmente pelo admin (add-on)
  extraGroupA: number;
  extraGroupB: number;
  monthlyTotal: number;
}

interface BillingReportResponse {
  success: boolean;
  data: BillingReportItem[];
}

export const getBillingReportService = (): ApiResponse<BillingReportResponse> =>
  api({ method: 'GET', url: 'clients/billing-report', useToken: true });

export const updateClientSubscriptionService = (
  clientId: string,
  data: {
    planName?: string;
    status?: SubscriptionStatus;
    extraGroupA?: number;
    extraGroupB?: number;
  }
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'PATCH',
    url: `clients/${clientId}/subscription`,
    data,
    useToken: true,
  });

// ─── Conta criada pelo admin (onboarding Enterprise) ────────────────────────

export interface CreateClientAccountData {
  name: string;
  email: string;
  isCompany: boolean;
  documentNumber: string;
  companyName: string;
  role: string; // cargo — obrigatório para PJ
  planName: string; // plano pago (o backend recusa Free)
  status: 'active' | 'trial';
}

// Cria a conta já com plano/assinatura e envia "Ative sua conta" ao cliente
export const createClientAccountService = (
  data: CreateClientAccountData
): ApiResponse<{ success: boolean; data: Client }> =>
  api({ method: 'POST', url: 'clients', data, useToken: true });

// Reenvia o link de ativação (novo token, 7 dias)
export const resendClientSetupLinkService = (
  clientId: string
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'POST',
    url: `clients/${clientId}/setup-link`,
    useToken: true,
  });

// ─── Reset de senha pelo admin ───────────────────────────────────────────────

// Envia ao cliente o e-mail do "Esqueci minha senha" (link de 6 h, uso único)
export const sendClientPasswordResetService = (
  clientId: string
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'POST',
    url: `clients/${clientId}/password-reset`,
    useToken: true,
  });

// Define senha provisória (o admin repassa ao cliente; recusada em conta não ativada)
export const setClientTemporaryPasswordService = (
  clientId: string,
  password: string
): ApiResponse<{ success: boolean }> =>
  api({
    method: 'PATCH',
    url: `clients/${clientId}/password`,
    data: { password },
    useToken: true,
  });
